/**
 * Formatting utilities — single source of truth for display formatting.
 *
 * All user-facing date / currency / quantity strings should use these helpers
 * so that locale, symbol, and precision are consistent across every page.
 *
 * Dates intentionally use an explicit numeric format instead of browser locale
 * defaults, so every screen displays the same DD-MM-YYYY convention.
 */

const DISPLAY_TIME_ZONE = "Asia/Kolkata";

function parseDateParts(value) {
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
  } else if (typeof value === "string") {
    // Date-only API values represent calendar dates, not UTC instants. Parse
    // their components directly so they cannot shift to the previous day.
    const dateOnly = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (dateOnly) {
      return { year: dateOnly[1], month: dateOnly[2], day: dateOnly[3] };
    }
  }

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: DISPLAY_TIME_ZONE,
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).formatToParts(date);
  const byType = Object.fromEntries(parts.map(({ type, value: partValue }) => [type, partValue]));
  return { year: byType.year, month: byType.month, day: byType.day };
}

function formatDateParts(parts) {
  return parts ? `${parts.day}-${parts.month}-${parts.year}` : null;
}

/**
 * Format a date string (ISO 8601 or YYYY-MM-DD) for human-readable display.
 * Returns "—" for null / empty. Example output: "17-07-2026"
 *
 * @param {string|Date|null} isoStr
 * @returns {string}
 */
export function fmtDate(isoStr) {
  if (!isoStr) return "—";
  try {
    return formatDateParts(parseDateParts(isoStr)) || String(isoStr);
  } catch {
    return String(isoStr);
  }
}

/**
 * Format a date for a long heading / greeting line.
 * Returns "—" for null / empty. Example output: "Thursday, 17-07-2026"
 *
 * @param {string|Date|null} isoStr
 * @returns {string}
 */
export function fmtDateFull(isoStr) {
  if (!isoStr) return "—";
  try {
    const parts = parseDateParts(isoStr);
    if (!parts) return String(isoStr);
    const date = isoStr instanceof Date
      ? isoStr
      : /^\d{4}-\d{2}-\d{2}$/.test(String(isoStr))
        ? new Date(`${isoStr}T12:00:00+05:30`)
        : new Date(isoStr);
    const weekday = new Intl.DateTimeFormat("en-IN", {
      timeZone: DISPLAY_TIME_ZONE,
      weekday: "long",
    }).format(date);
    return `${weekday}, ${formatDateParts(parts)}`;
  } catch {
    return String(isoStr);
  }
}

/**
 * Format a date showing day + month only — no year.
 * Used for compact chart bucket labels. Example output: "17-07".
 *
 * @param {string|Date|null} isoStr
 * @returns {string}
 */
export function fmtDateShort(isoStr) {
  if (!isoStr) return "—";
  try {
    const parts = parseDateParts(isoStr);
    return parts ? `${parts.day}-${parts.month}` : String(isoStr);
  } catch {
    return String(isoStr);
  }
}

/**
 * Format a datetime string (ISO 8601) for human-readable display.
 * Returns "—" for null / empty. Example output: "17-07-2026, 10:30 am"
 *
 * @param {string|null} isoStr
 * @returns {string}
 */
export function fmtDateTime(isoStr) {
  if (!isoStr) return "—";
  try {
    const date = new Date(isoStr);
    if (Number.isNaN(date.getTime())) return String(isoStr);
    const datePart = fmtDate(isoStr);
    const timePart = new Intl.DateTimeFormat("en-IN", {
      timeZone: DISPLAY_TIME_ZONE,
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
    return `${datePart}, ${timePart.format(date)}`;
  } catch {
    return String(isoStr);
  }
}

/**
 * Format a currency amount.
 * Returns "—" for null / empty / NaN. Example: "₹ 1,23,456.78"
 *
 * @param {number|string|null} n
 * @param {object|string} [opts]
 *   If a string, treated as the currency symbol (legacy compat).
 *   If an object: { symbol?: string, minDecimals?: number, maxDecimals?: number }
 * @returns {string}
 */
export function fmtCurrency(n, opts = {}) {
  const {
    symbol = "₹",
    minDecimals = 0,
    maxDecimals = 2,
  } = typeof opts === "string" ? { symbol: opts } : opts;

  if (n == null || n === "") return "—";
  const num = Number(n);
  if (isNaN(num)) return "—";
  return `${symbol} ${num.toLocaleString("en-IN", {
    minimumFractionDigits: minDecimals,
    maximumFractionDigits: maxDecimals,
  })}`;
}

/**
 * Format a quantity for display.
 * Strips trailing ".00" artefacts, uses Indian locale grouping, max 3 decimal places.
 * Appends `unit` if provided.
 * Returns "—" for null / empty.
 *
 * @param {number|string|null} n
 * @param {string} [unit]  optional unit suffix, e.g. "Nos"
 * @returns {string}
 */
export function fmtQty(n, unit) {
  if (n == null || n === "") return "—";
  const s = Number(n).toLocaleString("en-IN", { maximumFractionDigits: 3 });
  return unit ? `${s} ${unit}` : s;
}
